import { convertUploadsToAttachments, useStreamChatContext } from '../../utils/utils';
import { useChannelStateContext, useMessageInputContext } from 'stream-chat-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useStatsigContext } from '../../../elements-statsig-provider/utils';
import { disintermediationHelper } from '../../utils/disintermediation-helper';
import { doesContainEmail } from '../../../../../utils/validate-email';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import keys from 'lodash/keys';
import find from 'lodash/find';
import get from 'lodash/get';
import { AttachmentPreviewList } from './attachment-preview/attachment-preview-list';
import { AttachmentButton } from './attachment-button';
import { ValidationWarnings } from './validation-warnings';
import { InputError } from './input-error';
import { HostResponsiveness } from './host-responsiveness';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { MessageInput } from '@ps-design-system/components/MessageInput';

const MessageInputBase = ({ intl }) => {
  const {
    bookingState,
    bookingId,
    ssoId,
    savedMessageText,
    setSavedMessageText,
    savedAttachments,
    setSavedAttachments
  } = useStreamChatContext();

  const {
    text,
    setText,
    fileUploads,
    imageUploads,
    uploadNewFiles,
    handleChange,
    handleSubmit: originalHandleSubmit
  } = useMessageInputContext();

  const { members } = useChannelStateContext();

  const {
    featureGates,
    dynamicConfigs
  } = useStatsigContext();

  useEffect(() => {
    setSavedMessageText(text);
  }, [text]);

  useEffect(() => {
    const attachments = convertUploadsToAttachments(imageUploads, fileUploads);
    setSavedAttachments(attachments);
  }, [imageUploads, fileUploads]);

  useEffect(() => {
    setText(savedMessageText);
    uploadNewFiles(savedAttachments);
  }, []);

  const {
    isContactWarningEnabled,
    isDisintermediationEnabled,
    emailRegexString
  } = disintermediationHelper.getConfig(
    bookingState,
    featureGates?.contact_info_warning_gate?.(),
    featureGates?.disintermediation_gate?.(),
    dynamicConfigs?.disintermediation_email_regex?.() || {}
  );

  const [hasEmailBeenDetected, setHasEmailBeenDetected] = useState();
  const [hasPhoneNumberBeenDetected, setHasPhoneNumberBeenDetected] = useState();
  const [showDisintermediationModal, setShowDisintermediationModal] = useState(false);
  const [showContactInfoWarning, setShowContactInfoWarning] = useState(false);

  // Reset `hasEmailBeenDetected` if the user removes the email from the message
  useEffect(() => {
    if (isDisintermediationEnabled && !doesContainEmail(text, emailRegexString)) {
      setHasEmailBeenDetected(false);
    }
  }, [text, isDisintermediationEnabled]);

  const handleShowDisintermediation = () => {
    setHasEmailBeenDetected(true);
    setShowDisintermediationModal(true);
  };

  const handleShowPhoneWarning = () => {
    setHasPhoneNumberBeenDetected(true);
    setShowContactInfoWarning(true);
  };

  const handleSubmit = async (event) => {
    const shouldShowDisintermediationModal = disintermediationHelper.shouldShowDisintermediationModal(text, isDisintermediationEnabled, emailRegexString);
    if (shouldShowDisintermediationModal) {
      handleShowDisintermediation();
      analytics.track('viewed_email_block_notification', {
        bookingId,
        ssoId
      });
      return;
    }

    const shouldShowPhoneWarning = disintermediationHelper.shouldShowPhoneWarning(text, isContactWarningEnabled, hasPhoneNumberBeenDetected);
    if (shouldShowPhoneWarning) {
      handleShowPhoneWarning();
      analytics.track('viewed_phone_warning_notification', {
        bookingId,
        ssoId
      });
      return;
    }

    originalHandleSubmit(event);
  };

  const getInterlocutorName = () => {
    const ssoIds = keys(members);
    const interlocutorSSOid = find(ssoIds, (id) => id !== ssoId);
    return get(members, [interlocutorSSOid, 'user', 'name'], '');
  };

  const isSendDisabled = useMemo(() => {
    const hasAttachments = !isEmpty(fileUploads) || !isEmpty(imageUploads);
    const hasText = !isEmpty(trim(text));
    return (!hasText && !hasAttachments) || hasEmailBeenDetected;
  }, [text, hasEmailBeenDetected, fileUploads, imageUploads]);

  return (
    <div className="StreamMessageInput">
      <div className="MessageTextArea">
        <ValidationWarnings
          setShowContactInfoWarning={setShowContactInfoWarning}
          showContactInfoWarning={showContactInfoWarning}
          setShowDisintermediationModal={setShowDisintermediationModal}
          showDisintermediationModal={showDisintermediationModal}
        />
        <div className="button-input-container">
          {featureGates?.inbox_attachments?.() && <AttachmentButton/>}
          <div className="message-input-container">
            <AttachmentPreviewList/>
            <MessageInput
              disableSend={isSendDisabled}
              placeholder={intl.formatMessage({
                id: 'stream.message_input.message_placeholder',
                defaultMessage: 'Message {name}'
              }, { name: getInterlocutorName() })}
              value={text}
              onChange={handleChange}
              onSendPressed={handleSubmit}
              showBorder={false}
              shrunkSendButtonClass="send-button-shrunk"
            />
          </div>
        </div>
      </div>
      <InputError/>
      <div className="flex space-between margin-sm-top">
        <HostResponsiveness/>
      </div>
    </div>
  );
};

MessageInputBase.propTypes = {
  intl: PropTypes.object
};

export default injectIntl(MessageInputBase);
